// TODO(atoms): This component needs the following atoms from @visioni/a3s-ui:
//   - DataTable / VirtualList — scrollable list with sticky header, column definitions
//   - ColumnHeader — row of styled column labels for tabular layouts
// See: docs/plans/a3s-activity-atoms.md

import React, { useCallback, useMemo } from "react";
import { ActivityIndicator, type ListRenderItemInfo, useWindowDimensions } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import Animated from "react-native-reanimated";
import { View, Text, Button } from "@visioni/a3s-ui/components";
import { ActivityItemCard } from "../ActivityItemCard/ActivityItemCard";
import type { ActivityItemViewModel } from "../ActivityItemCard/ActivityItemCard";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ActivityListProps {
  /** Pre-fetched activity items to display. Sorting is handled internally. */
  readonly items: readonly ActivityItemViewModel[];
  /** Whether the list is currently loading (initial fetch). */
  readonly isLoading?: boolean;
  /** Whether a fetch error occurred. */
  readonly error?: boolean;
  /** Called when the camera label link is pressed. */
  readonly onCameraPress?: (item: ActivityItemViewModel) => void;
  /** Total count of matching records (for the "Showing X of Y" indicator). */
  readonly totalCount?: number | undefined;
  /** Whether more items can be loaded. */
  readonly hasNextPage?: boolean;
  /** Whether the next page is currently being fetched. */
  readonly isFetchingNextPage?: boolean;
  /** Load the next page of results. */
  readonly onLoadMore?: () => void;
  /** Render function for expandable detail content below a row. */
  readonly renderDetails?: (item: ActivityItemViewModel) => React.ReactNode;
}

// ---------------------------------------------------------------------------
// Column definitions — will become a DataTable atom prop
// ---------------------------------------------------------------------------

const COLUMNS = [
  { key: "summary", label: "Summary", flex: 3, align: undefined },
  { key: "scope", label: "Scope", flex: 2, align: undefined },
  { key: "user", label: "User", flex: 2, align: undefined },
  { key: "time", label: "Time", flex: 0.5, align: "right" as const },
] as const;

// ---------------------------------------------------------------------------
// ActivityList — molecule
// Renders a sorted flat list of activity items with a column header.
// Data fetching is the responsibility of the parent page/organism.
// ---------------------------------------------------------------------------

export function ActivityList({
  items,
  isLoading,
  error,
  onCameraPress,
  totalCount,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  renderDetails,
}: ActivityListProps) {
  const { theme } = useUnistyles();
  const { width } = useWindowDimensions();
  const isCompact = width < 800;

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [items],
  );

  const keyExtractor = useCallback((item: ActivityItemViewModel) => item.id, []);

  const renderItem = useCallback(
    ({ item, index }: ListRenderItemInfo<ActivityItemViewModel>) => (
      <ActivityItemCard
        item={item}
        isLast={index === sortedItems.length - 1}
        {...(onCameraPress ? { onCameraPress } : {})}
        {...(renderDetails ? { renderDetails } : {})}
      />
    ),
    [sortedItems, onCameraPress, renderDetails],
  );

  const listHeaderComponent = useMemo(
    () =>
      isCompact ? null : (
        <View bg="surface" style={{ overflow: "hidden" }}>
          <View
            flexDirection="row"
            alignItems="center"
            px="4xl"
            py="md"
            bg="surface"
            borderBottomWidth={1}
            borderColor="borderCard"
          >
            {COLUMNS.map((col) => (
              <View key={col.key} flex={col.flex} pr="md" style={{ minWidth: 0 }}>
                <Text
                  textStyle="xxs/medium"
                  color="textMuted"
                  style={col.align === "right" ? { textAlign: "right" } : undefined}
                >
                  {col.label}
                </Text>
              </View>
            ))}
            <View width={24} />
          </View>
        </View>
      ),
    [isCompact],
  );

  const listFooterComponent = useMemo(() => {
    const showCount = totalCount != null && items.length > 0;
    const showLoadMore = hasNextPage && onLoadMore;

    if (!showCount && !showLoadMore) return null;

    return (
      <View alignItems="center" py="4xl" gap="lg">
        {showCount && (
          <Text textStyle="xs/regular" color="textMuted">
            Showing {items.length} of {totalCount} results
          </Text>
        )}
        {showLoadMore && (
          <Button
            label="Load more"
            variant="secondary"
            variantColor="grey"
            size="sm"
            onPress={onLoadMore}
            loading={isFetchingNextPage ?? false}
            loadingText="Loading…"
          />
        )}
      </View>
    );
  }, [totalCount, items.length, hasNextPage, onLoadMore, isFetchingNextPage]);

  if (isLoading && items.length === 0) {
    return (
      <View flex={1} bg="background" alignItems="center" justifyContent="center">
        <ActivityIndicator size="large" color={theme.colors.text} />
        <Text textStyle="sm/regular" color="textMuted" style={{ marginTop: theme.spacing.lg }}>
          Loading history…
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <View flex={1} bg="background" alignItems="center" justifyContent="center" px="4xl">
        <Text textStyle="sm/medium" color="danger">
          Unable to load audit logs. Please try again later.
        </Text>
      </View>
    );
  }

  if (!isLoading && items.length === 0 && !hasNextPage) {
    return (
      <View flex={1} bg="background" alignItems="center" justifyContent="center">
        <Text textStyle="sm/regular" color="textMuted">
          No audit logs found
        </Text>
      </View>
    );
  }

  return (
    <View flex={1} bg="background">
      <Animated.FlatList
        data={sortedItems}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListHeaderComponent={listHeaderComponent}
        ListFooterComponent={listFooterComponent}
        removeClippedSubviews={false}
        windowSize={51}
        maxToRenderPerBatch={50}
        initialNumToRender={50}
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingTop: theme.spacing["4xl"],
          paddingBottom: theme.spacing["10xl"],
        }}
        showsVerticalScrollIndicator
        scrollIndicatorInsets={{ right: 0, top: 0, bottom: 0, left: 0 }}
      />
    </View>
  );
}
